import { WebEDIAnnotation } from 'services/webediErrors/types';
import portalSchema from 'support/types/schema-portal';

import { mapAnnotation, MapAnnotationContext } from './mapAnnotation';

type Annotation = portalSchema.components['schemas']['Annotation'];

// Memo invariants (BOTH must hold; violate either and stale items are served):
//
// 1. Annotation objects are immutable from this function's perspective. The
//    backend produces new references via TanStack Query; in-place mutation
//    would defeat the cache.
//
// 2. The resolved fieldPath must depend ONLY on the annotation, NOT on
//    ctx.canonical or ctx.flattenData. Today this holds because:
//      - resolveLineItemAnnotationItemId is a path passthrough (no flattenData)
//      - resolvePartyAnnotationItemId reads flattenData.parties, but parties
//        don't shift at runtime in current flows (user identifier edits do
//        not insert/remove parties)
//      - normalizeAnnotationPath reads ctx.canonical, but no path expansion
//        currently depends on canonical structure in a way that re-runs
//        produce different normalized paths
//
//    If a future annotation type breaks invariant #2 (e.g. a resolver whose
//    fieldPath depends on flattenData indices that *can* shift while the
//    annotation reference stays stable from TanStack Query), this cache will
//    silently return stale fieldPaths. The fix at that point is either a
//    composite cache key (WeakMap-of-WeakMaps on `ctx`) or invalidating the
//    cache when ctx references change.
const cache = new WeakMap<Annotation, WebEDIAnnotation | null>();

export const memoizedMapAnnotation = (annotation: Annotation, ctx: MapAnnotationContext): WebEDIAnnotation | null => {
  if (cache.has(annotation)) {
    return cache.get(annotation) ?? null;
  }
  const result = mapAnnotation(annotation, ctx);
  cache.set(annotation, result);
  return result;
};
